import { startOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { isEqual } from "lodash";
import { combineReducers } from "redux";
import { createSelector } from "reselect";
import { getType } from "typesafe-actions";
import { RootAction, RootState } from "../";
import { IElement } from "../elements/types";
import { getNextEvent } from "../events/reducers";
import { getTeamsById } from "../teams/reducers";
import * as actions from "./actions";
import {
  FixturesByEvent,
  IFixture,
  IFixtureGroup,
  IFixturesForEventByTeam,
  IGroupedFixturesByEvent,
  IState,
} from "./types";

// State Reducer
export default combineReducers<IState, RootAction>({
  byEvent: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(actions.addFixturesForEvent.success):
        return {
          ...state,
          [action.payload.event]: action.payload.data,
        };
      case getType(actions.addFixtures.success): {
        const eventSlice: FixturesByEvent = {};
        action.payload.forEach((f) => {
          if (f.event) {
            if (!eventSlice[f.event]) {
              eventSlice[f.event] = [];
            }
            eventSlice[f.event].push(f);
          }
        });
        return {
          ...state,
          ...eventSlice,
        };
      }
      default:
        return state;
    }
  },
  allStatsById: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(actions.addAllStatsForFixture.success):
        return {
          ...state,
          [action.payload.fixture]: action.payload.data,
        };
      default:
        return state;
    }
  },
});

// State Selectors
export const getFixturesByEvent = (state: RootState) => state.fixtures.byEvent;

export const getGroupedFixturesByEvent = createSelector(
  [(state: RootState) => getFixturesByEvent(state)],
  (fixturesByEvent) => {
    const groupedFixturesByEvent: IGroupedFixturesByEvent = {};

    Object.keys(fixturesByEvent).forEach((eventId) => {
      const fixtures = fixturesByEvent[eventId];
      const groups: IFixtureGroup[] = [];
      let currentGroup: IFixtureGroup | null = null;
      fixtures.forEach((fixture: IFixture) => {
        if (fixture.kickoff_time) {
          const kickoffDate = new Date(fixture.kickoff_time);
          const fixtureDate = startOfDay(
            toZonedTime(
              kickoffDate,
              Intl.DateTimeFormat().resolvedOptions().timeZone,
            ),
          );
          if (
            currentGroup == null ||
            !isEqual(fixtureDate, currentGroup.date)
          ) {
            currentGroup = {
              date: fixtureDate,
              fixtures: [],
            };
            groups.push(currentGroup);
          }
          currentGroup.fixtures.push({ kickoffDate, ...fixture });
        }
      });
      groupedFixturesByEvent[eventId] = groups;
    });
    return groupedFixturesByEvent;
  },
);

export const getFixturesForEvent = (state: RootState, eventId: number) =>
  state.fixtures.byEvent[eventId] || [];

export const getFixturesForEventById = (state: RootState, eventId: number) => {
  const fixtures = getFixturesForEvent(state, eventId);
  if (!fixtures.length) {
    return null;
  }
  return fixtures.reduce((memo, f) => ({ ...memo, [f.id]: f }), {});
};

export const getFixturesForEventByTeam = createSelector(
  [
    (state: RootState) => getTeamsById(state),
    (state: RootState, eventId: number) => getFixturesForEvent(state, eventId),
  ],
  (teamsById, fixtures) => {
    const data: IFixturesForEventByTeam = Object.keys(teamsById).reduce(
      (memo, id) => ({
        ...memo,
        [id]: [],
      }),
      {},
    );
    fixtures.forEach((f) => {
      data[f.team_h].push(f);
      data[f.team_a].push(f);
    });
    return data;
  },
);

export const getFixturesForNextEventByTeam = (state: RootState) => {
  const nextEvent = getNextEvent(state);
  return getFixturesForEventByTeam(state, nextEvent ? nextEvent.id : 0);
};

export const getFixturesForTeamInEvent = createSelector(
  [
    (state: RootState, eventId: number) =>
      getFixturesForEventByTeam(state, eventId),
    (_: RootState, __: number, teamId: number) => teamId,
  ],
  (fixturesForEventByTeam, teamId) => fixturesForEventByTeam[teamId] ?? [],
);

export const getFixturesStatusForTeamInEvent = createSelector(
  [
    (state: RootState, eventId: number, teamId: number) =>
      getFixturesForTeamInEvent(state, eventId, teamId),
  ],
  (teamEventFixtures) => {
    if (teamEventFixtures.length === 0) {
      return {
        hasStarted: false,
        hasLive: false,
        allFinished: false,
      };
    }

    return {
      hasStarted: teamEventFixtures.some((fixture) => fixture.started),
      hasLive: teamEventFixtures.some(
        (fixture) => !fixture.finished_provisional && fixture.started,
      ),
      allFinished: teamEventFixtures.every(
        (fixture) => fixture.finished_provisional,
      ),
    };
  },
);

export const getElementFixtureOpponent = createSelector(
  [
    (state) => state.teams.byId,
    (_: RootState, element: IElement) => element,
    (_: RootState, __: IElement, fixture) => fixture,
  ],
  (teamsById, element, fixture) => {
    const isHome = element.team === fixture.team_h;
    const neutralIds: number[] = [];
    const suffix =
      neutralIds.indexOf(fixture.id) > -1 ? "(N)" : isHome ? "(H)" : "(A)";
    const oppTeam = isHome
      ? teamsById[fixture.team_a]
      : teamsById[fixture.team_h];
    return `${oppTeam.short_name} ${suffix}`;
  },
);

export const getElementEventOpponents = createSelector(
  [
    (state: RootState) => state.teams.byId,
    (_: RootState, element: IElement) => element,
    (_: RootState, __: IElement, fixtures: IFixture[]) => fixtures,
  ],
  (teamsById, element, fixtures) =>
    fixtures.map((f: IFixture) =>
      getElementFixtureOpponent(
        { teams: { byId: teamsById } } as RootState,
        element,
        f,
      ),
    ),
);

export const getAllStatsForFixture = (state: RootState, fixtureId: number) =>
  state.fixtures.allStatsById[fixtureId] || null;
